import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Save, Upload, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Convierte rutas relativas de uploads en URL absoluta del backend
const resolveImg = (src) =>
  !src ? "" : src.startsWith("/api/") ? `${BACKEND_URL}${src}` : src;

export default function AdminEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);

  const [form, setForm] = useState({
    title: "", excerpt: "", content: "", cover_image: "", tags: "", published: true,
  });
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState("");

  useEffect(() => {
    document.title = editing ? "Editar artículo | Distópica" : "Nuevo artículo | Distópica";
    if (editing) {
      api.get(`/blog/admin/posts/${id}`).then((r) => {
        const p = r.data;
        setForm({
          title: p.title, excerpt: p.excerpt, content: p.content,
          cover_image: p.cover_image, tags: (p.tags || []).join(", "), published: p.published,
        });
        setPreview(resolveImg(p.cover_image));
        setLoading(false);
      }).catch(() => { toast.error("No se pudo cargar el artículo"); navigate("/admin"); });
    }
  }, [id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seleccioná una imagen"); return; }
    // Vista previa local inmediata
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cover_image: data.url }));
      setPreview(resolveImg(data.url));
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo subir la imagen");
      setPreview(resolveImg(form.cover_image));
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    setForm((f) => ({ ...f, cover_image: "" }));
    setPreview("");
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    const payload = {
      title: form.title, excerpt: form.excerpt, content: form.content,
      cover_image: form.cover_image, published: form.published,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/blog/admin/posts/${id}`, payload);
      else await api.post("/blog/admin/posts", payload);
      toast.success(editing ? "Artículo actualizado" : "Artículo creado");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-white/15 px-4 py-3 text-base placeholder:text-neutral-600 focus:border-[var(--yellow)] focus:outline-none transition-colors rounded-sm";
  const labelCls = "font-mono-x text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2 block";

  if (loading) {
    return <main className="bg-[#0A0A0A] min-h-screen flex items-center justify-center"><p className="font-mono-x text-sm text-neutral-500">Cargando…</p></main>;
  }

  return (
    <main className="bg-[#0A0A0A] min-h-screen">
      <Toaster position="top-center" theme="dark" richColors />
      <header className="border-b border-white/10 sticky top-0 z-40 backdrop-blur-xl bg-black/60">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/admin" className="font-mono-x text-xs uppercase tracking-[0.2em] text-neutral-300 hover:text-[var(--yellow)] transition-colors flex items-center gap-2">
            <ArrowLeft size={16} /> Volver
          </Link>
          <button onClick={save} disabled={saving} data-testid="save-post" className="inline-flex items-center gap-2 bg-[var(--fuchsia)] text-white font-mono-x uppercase text-sm tracking-[0.15em] px-6 py-2.5 rounded-sm hover:bg-[var(--yellow)] hover:text-black transition-colors disabled:opacity-60">
            <Save size={16} /> {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </header>

      <form onSubmit={save} className="max-w-[1200px] mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-2 gap-10">
        <div className="flex flex-col gap-6">
          <h1 className="font-display uppercase text-3xl tracking-tight">{editing ? "Editar artículo" : "Nuevo artículo"}</h1>
          <div>
            <label className={labelCls}>Título *</label>
            <input data-testid="post-title" className={inputCls} value={form.title} onChange={set("title")} placeholder="Título del artículo" />
          </div>
          <div>
            <label className={labelCls}>Resumen / Excerpt</label>
            <textarea data-testid="post-excerpt" className={`${inputCls} resize-none`} rows={2} value={form.excerpt} onChange={set("excerpt")} placeholder="Breve descripción para SEO y tarjetas" />
          </div>
          <div>
            <label className={labelCls}>Imagen de portada</label>
            <input
              id="cover-file"
              type="file"
              accept="image/*"
              onChange={onPickFile}
              className="hidden"
              data-testid="post-cover-file"
            />
            <div className="flex items-center gap-3">
              <label
                htmlFor="cover-file"
                data-testid="post-cover-btn"
                className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 rounded-sm font-mono-x text-xs uppercase tracking-[0.15em] cursor-pointer hover:border-[var(--yellow)] hover:text-[var(--yellow)] transition-colors"
              >
                <Upload size={16} /> {uploading ? "Subiendo…" : "Adjuntar imagen"}
              </label>
              {preview && (
                <button type="button" onClick={clearImage} data-testid="post-cover-clear" className="p-2 text-neutral-400 hover:text-[var(--fuchsia)] transition-colors" title="Quitar imagen">
                  <X size={18} />
                </button>
              )}
            </div>
            {preview && (
              <div className="mt-3 relative border border-white/10 overflow-hidden">
                <img src={preview} alt="Portada" className="w-full aspect-[16/9] object-cover" data-testid="post-cover-preview" />
              </div>
            )}
            <p className="font-mono-x text-[11px] text-neutral-600 mt-2">JPG, PNG o WebP · máx 15 MB</p>
          </div>
          <div>
            <label className={labelCls}>Tags (separados por coma)</label>
            <input data-testid="post-tags" className={inputCls} value={form.tags} onChange={set("tags")} placeholder="DTF, Bordado, Branding" />
          </div>
          <div>
            <label className={labelCls}>Contenido (Markdown)</label>
            <textarea data-testid="post-content" className={`${inputCls} font-mono-x text-sm resize-y`} rows={16} value={form.content} onChange={set("content")} placeholder={"## Subtítulo\n\nTu texto aquí. Usá **negrita**, listas y ### subtítulos."} />
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input data-testid="post-published" type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} className="w-4 h-4 accent-[var(--fuchsia)]" />
            <span className="font-mono-x text-xs uppercase tracking-widest text-neutral-300">Publicado (visible en el sitio)</span>
          </label>
        </div>

        {/* Preview */}
        <div className="lg:border-l lg:border-white/10 lg:pl-10">
          <p className={labelCls}>Vista previa</p>
          {preview && <img src={preview} alt="" className="w-full aspect-[16/9] object-cover border border-white/10 mb-6" />}
          <h2 className="font-display uppercase text-3xl leading-tight tracking-tight mb-4">{form.title || "Título del artículo"}</h2>
          <div className="prose-preview">
            <ReactMarkdown components={{
              h2: (p) => <h3 className="font-display uppercase text-2xl mt-8 mb-3" {...p} />,
              h3: (p) => <h4 className="font-display uppercase text-xl mt-6 mb-2 text-[var(--yellow)]" {...p} />,
              p: (p) => <p className="text-neutral-300 leading-relaxed mb-4" {...p} />,
              ul: (p) => <ul className="list-disc pl-5 space-y-2 mb-4 text-neutral-300" {...p} />,
              ol: (p) => <ol className="list-decimal pl-5 space-y-2 mb-4 text-neutral-300" {...p} />,
              strong: (p) => <strong className="text-white" {...p} />,
            }}>{form.content || "_El contenido aparecerá acá…_"}</ReactMarkdown>
          </div>
        </div>
      </form>
    </main>
  );
}
